/** Lib Imports */
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";

import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import Toast from "@moltech/molui/toast";

/** Components */
import NotificationItem from "./NotificationItem";

/** Utilities */
import { handleNotificationRedirect, markSelectedNotificationsAsRead } from "../../../utils/common/notificationRedirect";
import {
  isNotificationUnread,
  normalizeNotificationItemsResponse,
} from "../../../utils/common/notificationDisplay";

/** RTK Query */
import {
  useLazyGetRecentNotificationsQuery,
  useMarkAllNotificationsAsReadMutation,
  useMarkNotificationAsReadMutation,
} from "../../../app/services/notificationApi";

/** CSS */
import "./Notifications.scss";

const RECENT_NOTIFICATION_QUERY = {
  pageNumber: 1,
  pageSize: 10,
};

const getUnreadStatusText = (unreadCount) => {
  if (!unreadCount) {
    return "You are all caught up";
  }

  return `${unreadCount} unread in your latest updates`;
};

const getNotificationKey = (notification) =>
  notification?.notificationId ??
  `${notification?.createdAt}-${notification?.title}`;

const NotificationDropdown = ({ visible, onRequestClose }) => {
  const navigate = useNavigate();

  const [pendingNotificationId, setPendingNotificationId] = useState(null);
  const [selectedNotificationIds, setSelectedNotificationIds] = useState([]);
  const [selectionMode, setSelectionMode] = useState(false);

  const [
    fetchRecentNotifications,
    {
      data,
      isLoading,
      isFetching,
      isError,
    },
  ] = useLazyGetRecentNotificationsQuery();

  const [markNotificationAsRead, { isLoading: isMarkingNotificationAsRead }] =
    useMarkNotificationAsReadMutation();

  const [
    markAllNotificationsAsRead,
    { isLoading: isMarkingAllNotificationsAsRead },
  ] = useMarkAllNotificationsAsReadMutation();

  useEffect(() => {
    if (!visible) {
      setSelectionMode(false);
      setSelectedNotificationIds([]);
      setPendingNotificationId(null);
      return;
    }

    const loadNotifications = async () => {
      const result = await fetchRecentNotifications(RECENT_NOTIFICATION_QUERY);

      if (result?.error) {
        Toast.error("Unable to load notifications right now.");
      }
    };

    loadNotifications();
  }, [fetchRecentNotifications, visible]);

  const normalizedResponse = normalizeNotificationItemsResponse(
    data,
    RECENT_NOTIFICATION_QUERY.pageSize,
  );

  const notifications = normalizedResponse.items;
  const unreadNotifications = notifications.filter(isNotificationUnread);
  const unreadCount = unreadNotifications.length;

  const selectedUnreadCount = unreadNotifications.filter((item) =>
    selectedNotificationIds.includes(item.notificationId),
  ).length;

  const isMutating =
    isMarkingNotificationAsRead || isMarkingAllNotificationsAsRead;

  const isAllUnreadSelected =
    unreadCount > 0 && selectedUnreadCount === unreadCount;

  const unreadStatusText = getUnreadStatusText(unreadCount);
  const headerActionLabel = selectionMode ? "Cancel" : "Mark All as Read";
  const footerActionLabel = selectionMode
    ? `Mark Selected as Read (${selectedUnreadCount})`
    : "View All";
  const footerStatusText =
    isFetching && !isLoading ? "Refreshing..." : "Latest 10 notifications";

  const isHeaderActionDisabled = !selectionMode && (!unreadCount || isMutating);
  const isFooterActionDisabled =
    selectionMode && (!selectedUnreadCount || isMutating);

  const handleViewAll = () => {
    onRequestClose?.();
    navigate("/notifications");
  };

  const clearPendingNotification = () => {
    setPendingNotificationId(null);
  };

  const handleNotificationClick = async (notification) => {
    if (isMutating) {
      return;
    }

    setPendingNotificationId(notification?.notificationId ?? null);

    await handleNotificationRedirect({
      notification,
      navigate,
      markNotificationAsRead,
      onBeforeNavigate: onRequestClose,
      onComplete: clearPendingNotification,
    });

    clearPendingNotification();
  };

  const toggleSelectionMode = () => {
    setSelectionMode((prev) => !prev);
    setSelectedNotificationIds([]);
  };

  const handleSelectionChange = (notification, checked) => {
    setSelectedNotificationIds((prev) => {
      if (checked) {
        return [...prev, notification.notificationId];
      }

      return prev.filter((id) => id !== notification.notificationId);
    });
  };

  const handleSelectAllUnreadChange = (event) => {
    if (!event.checked) {
      setSelectedNotificationIds([]);
      return;
    }

    setSelectedNotificationIds(
      unreadNotifications.map((item) => item.notificationId),
    );
  };

  const handleMarkSelectedAsRead = async () => {
    if (!selectedUnreadCount || isMutating) {
      return;
    }

    const result = await markSelectedNotificationsAsRead({
      notifications,
      notificationIds: selectedNotificationIds,
      markNotificationAsRead,
    });

    if (!result.success) {
      return;
    }

    Toast.success("Selected notifications marked as read.");
    setSelectedNotificationIds([]);
    setSelectionMode(false);
  };

  const handleMarkAllAsRead = async () => {
    if (!unreadCount || isMutating) {
      return;
    }

    try {
      await markAllNotificationsAsRead().unwrap();
    } catch {
      Toast.error("Unable to mark notifications as read right now.");
    }
  };

  const handleHeaderActionClick = () => {
    if (selectionMode) {
      toggleSelectionMode();
      return;
    }

    handleMarkAllAsRead();
  };

  const handleFooterActionClick = () => {
    if (selectionMode) {
      handleMarkSelectedAsRead();
      return;
    }

    handleViewAll();
  };

  const handleRetry = () => {
    fetchRecentNotifications(RECENT_NOTIFICATION_QUERY);
  };

  const renderLoadingState = () => (
    <div className="notification-dropdown__state">
      <i className="pi pi-spin pi-spinner" />
      <p>Loading your latest notifications...</p>
    </div>
  );

  const renderErrorState = () => (
    <div className="notification-dropdown__state">
      <i className="pi pi-exclamation-circle" />
      <p>Unable to load notifications right now.</p>
      <Button
        label="Retry"
        className="btn secondary-btn p-button-sm"
        onClick={handleRetry}
      />
    </div>
  );

  const renderEmptyState = () => (
    <div className="notification-dropdown__state">
      <i className="pi pi-inbox" />
      <p>No notifications yet.</p>
    </div>
  );

  const renderNotificationList = () =>
    notifications.map((notification) => (
      <NotificationItem
        key={getNotificationKey(notification)}
        notification={notification}
        mode="compact"
        disabled={isMutating}
        isActionLoading={
          pendingNotificationId === notification?.notificationId
        }
        onClick={handleNotificationClick}
        selectionMode={selectionMode}
        isSelected={selectedNotificationIds.includes(
          notification?.notificationId,
        )}
        onSelectionChange={handleSelectionChange}
        isNotificationDropdown={true}
      />
    ));

  let bodyContent = renderNotificationList();

  if (isLoading) {
    bodyContent = renderLoadingState();
  } else if (isError) {
    bodyContent = renderErrorState();
  } else if (!notifications.length) {
    bodyContent = renderEmptyState();
  }

  return (
    <div className="notification-dropdown">
      <div className="notification-dropdown__header">
        <div className="notification-dropdown__title">
          <h3>Notifications</h3>
          <span>{unreadStatusText}</span>
        </div>

        <div className="notification-dropdown__actions">
          {selectionMode && (
            <Checkbox
              checked={isAllUnreadSelected}
              disabled={!unreadCount || isMutating}
              onChange={handleSelectAllUnreadChange}
              aria-label="Select all unread notifications"
            />
          )}

          <Button
            label={headerActionLabel}
            className="btn secondary-btn p-button-sm"
            disabled={isHeaderActionDisabled}
            onClick={handleHeaderActionClick}
          />
        </div>
      </div>

      <div className="notification-dropdown__body">{bodyContent}</div>

      <div className="notification-dropdown__footer">
        <span>{footerStatusText}</span>

        <div className="notification-dropdown__footer-actions">
          <Button
            label={footerActionLabel}
            className="btn secondary-btn p-button-sm"
            disabled={isFooterActionDisabled}
            onClick={handleFooterActionClick}
          />
        </div>
      </div>
    </div>
  );
};

NotificationDropdown.propTypes = {
  visible: PropTypes.bool,
  onRequestClose: PropTypes.func,
};

export default NotificationDropdown;
